import {
  adminBoltOnDischargesVars,
  defaultBoltOnDischargesVars,
  deleteBoltOnDischarges,
  ecopetrolBoltOnDischargesVars,
  kocBoltOnDischargesVars,
  restoreBoltOnDischarges
} from "@/models/equipment/bolt_on_discharges/predicates";
import type { Bolt_On_Discharges } from "gql/api";
import {
  useBoltOnDischargesQuery,
  useDeleteBoltOnDischargesMutation,
  useInsertBoltOnDischargeMutation,
  useRestoreBoltOnDischargesMutation,
  useUpdateBoltOnDischargeByPkMutation
} from "gql/api";
import { v4 } from "uuid";
import type { ComputedRef } from "vue";
import { computed } from "vue";
import { useEquipment } from "../baseEquipment";
import type { UseEquipment } from "../equipmentTypes";
import type { BoltOnDischargeRowData } from "./boltOnDischargeTableColumns";
import { boltOnDischargeTableColumns } from "./boltOnDischargeTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

/**
 * The generated row type carries reverse relationships that a Hasura insert or
 * update input does not accept. Drop them, the same way the motors composable
 * drops its own relationship fields.
 */
type BoltOnDischargeInput = Omit<
  BoltOnDischargeRowData,
  | "user"
  | "scenario_equipment_revisions"
  | "scenario_equipment_revisions_aggregate"
  | "lower_scenario_equipment_revisions"
  | "lower_scenario_equipment_revisions_aggregate"
>;

export function useBoltOnDischarges(
  isAdmin: boolean
): UseEquipment<Bolt_On_Discharges> {
  const baseEquipment = useEquipment<Bolt_On_Discharges>(isAdmin);
  const title = "Bolt On Discharges";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocBoltOnDischargesVars;
    if (user.is_ecopetrol_customer) return ecopetrolBoltOnDischargesVars;
    if (user.is_admin) return adminBoltOnDischargesVars;
    return defaultBoltOnDischargesVars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useBoltOnDischargesQuery({
    variables: getVariables()
  });

  const allRows: ComputedRef<BoltOnDischargeRowData[]> = computed(
    () => (data.value?.bolt_on_discharges as Bolt_On_Discharges[]) || []
  );

  const hashOrder: (keyof Bolt_On_Discharges)[] = [
    "manufacturer",
    "series_model",
    "tubing_diameter_inches",
    "outer_diameter_inches",
    "flange_outer_diameter_inches"
  ];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsert } = useInsertBoltOnDischargeMutation();
  const { executeMutation: executeUpdate } =
    useUpdateBoltOnDischargeByPkMutation();
  const { executeMutation: executeDelete } =
    useDeleteBoltOnDischargesMutation();
  const { executeMutation: executeRestore } =
    useRestoreBoltOnDischargesMutation();

  const insertBoltOnDischarge = (object: BoltOnDischargeInput) =>
    executeInsert({ object });

  const updateBoltOnDischarge = (id: string, object: BoltOnDischargeInput) =>
    executeUpdate({ id, object });

  const deleteBoltOnDischarge = (ids: string[]) =>
    executeDelete({ where: deleteBoltOnDischarges(ids) });

  const restoreBoltOnDischarge = (ids: string[]) =>
    executeRestore({ where: restoreBoltOnDischarges(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "bolt_on_discharges",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    manufacturer: "",
    series_model: "",
    is_hps: false,
    outer_diameter_inches: null,
    inner_diameter_inches: null,
    flange_outer_diameter_inches: null,
    neck_outer_diameter_inches: null,
    neck_inner_diameter_inches: null,
    length_inches: null,
    weight_pounds: null,
    tubing_diameter_inches: null,
    include_koc_timestamp: null,
    only_koc_timestamp: null,
    include_ecopetrol_timestamp: null,
    only_ecopetrol_timestamp: null
  }));

  return {
    searchPlaceholder: "Search Bolt On Discharges...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "DSCG BTN 400"`,
    columns: defineVisibleColumns(boltOnDischargeTableColumns),
    handleUpsert: defineUpsert(insertBoltOnDischarge, updateBoltOnDischarge),
    handleDelete: defineDelete(deleteBoltOnDischarge),
    handleRestore: defineRestore(restoreBoltOnDischarge),
    totalCount: computed(
      () => data.value?.bolt_on_discharges_aggregate.aggregate?.count
    ),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: boltOnDischargeTableColumns,
      globalSearchFields: ["manufacturer", "series_model"],
      allRows,
      title
    })
  };
}
